import {lazy, Suspense} from 'react'
import {BrowserRouter, Navigate, Route, Routes} from 'react-router-dom'
import './utils/rum'
import toast, {Toaster, ToastBar} from 'react-hot-toast'

import Layout from './components/layout/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import RoleProtectedRoute from './components/navigation/RoleProtectedRoute'
import RootRoute from './components/navigation/RootRoute'
import ScrollToTop from './components/ScrollToTop'
import ErrorBoundary from './components/ErrorBoundary'
import ComponentErrorBoundary from './components/ComponentErrorBoundary'
import PageLoader from './components/loading/PageLoader'
import AppContextProvider from './contexts/AppContextProvider'
import {BreadcrumbProvider} from './contexts/BreadcrumbContext'
import {NavBackProvider} from './contexts/NavBackContext'
import OAuth2Callback from './components/auth/OAuth2Callback'
import AppInitializer from './components/auth/AppInitializer'
import {usePageView} from './hooks/usePageView'
import UpdateModal from './components/UpdateModal'

import LoginPage from './pages/login/LoginPage'
import SignupPage from './pages/signup/SignupPage'
import VerifyEmailPendingPage from './pages/signup/VerifyEmailPendingPage'
import EmailVerificationPage from './pages/signup/EmailVerificationPage'

const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'))
const ScorecardPage = lazy(() => import('./pages/scorecard/ScorecardEntryPage'))
const MultiplayerRoundSetupPage = lazy(() => import('./pages/scorecard/multiplayer/MultiplayerRoundSetupPage'))
const GroupScoringPage = lazy(() => import('./pages/scorecard/multiplayer/GroupScoringPage'))
const ScoreCardViewPage = lazy(() => import('./pages/scorecard/ScoreCardViewPage'))
const ProfilePage = lazy(() => import('./pages/user/ProfilePage'))
const ConfirmEmailChangePage = lazy(() => import('./pages/user/ConfirmEmailChangePage'))
const GolfIdLoginPage = lazy(() => import('./pages/login/GolfIdLoginPage'))
const JoinSocietyPage = lazy(() => import('./pages/join/JoinSocietyPage'))
const ForgotPasswordPage = lazy(() => import('./pages/login/ForgotPassword'))
const ResetPasswordPage = lazy(() => import('./pages/login/ResetPassword'))

const CompetitionsPage = lazy(() => import('./pages/competitions/CompetitionsPage'))
const CompetitionCreatePage = lazy(() => import('./pages/competitions/admin/CompetitionCreatePage'))
const CompetitionEditPage = lazy(() => import('./pages/competitions/admin/CompetitionEditPage'))
const CompetitionViewPage = lazy(() => import('./pages/competitions/player/CompetitionViewPage'))
const AdministrationPage = lazy(() => import('./pages/AdministrationPage'))
const ReportsPage = lazy(() => import('./pages/ReportsPage'))
const SettingsPage = lazy(() => import('./pages/user/SettingsPage'))
const TenantsPage = lazy(() => import('./pages/tenants/TenantsPage'))
const TenantCreatePage = lazy(() => import('./pages/tenants/TenantCreatePage'))
const TenantEditPage = lazy(() => import('./pages/tenants/TenantEditPage'))

const ScorecardManagementPage = lazy(() => import('./pages/scorecard/admin/ScorecardManagementPage'))
const ScorecardEditPage = lazy(() => import('./pages/scorecard/admin/ScorecardEditPage'))
const CourseAnalyticsPage = lazy(() => import('./pages/admin/analytics/CourseAnalyticsPage'))
const AnalyticsDashboardPage = lazy(() => import('./pages/admin/analytics/AnalyticsDashboardPage'))
const AdminUsersPage = lazy(() => import('./pages/user/admin/UsersPage'))
const CreateUserPage = lazy(() => import('./pages/user/admin/CreateUserPage'))
const EditUserPage = lazy(() => import('./pages/user/admin/EditUserPage'))

const ProfileCompletionPage = lazy(() => import('./pages/signup/ProfileCompletionPage'))

const FriendsPage = lazy(() => import('./pages/friends/FriendsPage'))
const FriendScorecardPage = lazy(() => import('./pages/friends/FriendScorecardPage'))
const FriendProfilePage = lazy(() => import('./pages/friends/FriendProfilePage'))

const StatsDashboardPage = lazy(() => import('./pages/stats/StatsDashboardPage'))
const TimeScheduleTemplatesManager = lazy(() => import('./pages/competitions/admin/time-schedule/TimeScheduleTemplatesManager'))
const TimeScheduleTemplateCreatePage = lazy(() => import('./pages/competitions/admin/time-schedule/TimeScheduleTemplateCreatePage'))
const TimeScheduleTemplateEditPage = lazy(() => import('./pages/competitions/admin/time-schedule/TimeScheduleTemplateEditPage'))
const PaymentResultPage = lazy(() => import('./pages/payment/PaymentResultPage'))
const PaymentReportPage = lazy(() => import('./pages/admin/payments/PaymentReportPage'))
const PaymentHistoryPage = lazy(() => import('./pages/user/PaymentHistoryPage'))

const CategoriesPage = lazy(() => import('./pages/competitions/admin/categories/CategoriesPage'))
const RankingSeriesListPage = lazy(() => import('./pages/rankings/admin/RankingSeriesListPage'))
const RankingSeriesFormPage = lazy(() => import('./pages/rankings/admin/RankingSeriesFormPage'))
const RankingEditPage = lazy(() => import('./pages/rankings/admin/RankingEditPage'))
const PointSystemsPage = lazy(() => import('./pages/rankings/admin/PointSystemsPage'))

const PlayerRankingsPage = lazy(() => import('./pages/rankings/player/RankingsPage'))
const PlayerRankingViewPage = lazy(() => import('./pages/rankings/player/RankingViewPage'))
const FlightScoringPage = lazy(() => import('./pages/competitions/player/FlightScoringPage'))
const MatchScoringPage = lazy(() => import('./pages/competitions/player/MatchScoringPage'))
const MatchResultReviewPage = lazy(() => import('./pages/competitions/player/MatchResultReviewPage'))
const FlightScorecardReviewPage = lazy(() => import('./pages/competitions/player/FlightScorecardReviewPage'))

const TvLeaderboardPage = lazy(() => import('./pages/public/tv/TvLeaderboardPage'))
const PublicCompetitionCalendarPage = lazy(() => import('./pages/public/calendar/PublicCompetitionCalendarPage'))
const PublicCompetitionDetailPage = lazy(() => import('./pages/public/competition/PublicCompetitionDetailPage'))
const PublicRankingListPage = lazy(() => import('./pages/public/rankings/PublicRankingListPage'))
const PublicRankingDetailPage = lazy(() => import('./pages/public/rankings/PublicRankingDetailPage'))
const HandicapRecommendationsPage = lazy(() => import('./pages/admin/handicap-recommendations/HandicapRecommendationsPage'))
const HandicapRecommendationCreatePage = lazy(() => import('./pages/admin/handicap-recommendations/HandicapRecommendationCreatePage'))
const HandicapRecommendationEditPage = lazy(() => import('./pages/admin/handicap-recommendations/HandicapRecommendationEditPage'))
const EmailLogPage = lazy(() => import('./pages/admin/email-log/EmailLogPage'))
const AddOnsPage = lazy(() => import('./pages/admin/add-ons/AddOnsPage'))
const CoursePollsPage = lazy(() => import('./pages/admin/polls/CoursePollsPage'))


const PageViewTracker: React.FC = () => {
    usePageView()
    return null
}

const App: React.FC = () => {
    // Preview mode indicator - check if BASE_PATH contains '/preview/'
    const isPreviewMode = import.meta.env.VITE_BASE_PATH && import.meta.env.VITE_BASE_PATH.includes('/preview/')
    const previewBranch = import.meta.env.VITE_BRANCH_NAME || 'preview'

    // Always use BrowserRouter now that CloudFront Function handles routing
    const Router = BrowserRouter

    // Get basename for router - needs to match the base path set during build
    const getBasename = (): string => {
        // For preview deployments, use the base path from environment
        if (isPreviewMode && import.meta.env.VITE_BASE_PATH) {
            // Remove trailing slash for React Router basename
            return import.meta.env.VITE_BASE_PATH.replace(/\/$/, '')
        }
        // In production/development, use root
        return '/'
    }

    const appContent = (
        <ErrorBoundary>
            <Router basename={getBasename()}>
                <ScrollToTop />
                <PageViewTracker />
                <AppContextProvider>
                    <BreadcrumbProvider>
                        <NavBackProvider>
                        <AppInitializer>
                            <div className="App min-vh-100">
                                    {/* Preview mode banner */}
                                    {isPreviewMode && (
                                        <div className="bg-warning text-dark text-center py-2" style={{
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 1050,
                                            borderBottom: '2px solid #ffc107'
                                        }}>
                                            <strong>🚧 Preview Deployment</strong> - Branch: <code>{previewBranch}</code>
                                            <br />
                                            <small>This is a preview deployment for testing UI changes.</small>
                                        </div>
                                    )}
                                    <Routes>
                                    <Route path="/" element={<RootRoute/>}/>
                                    <Route path="/index.html" element={<Navigate to="/login" replace/>}/>

                                    <Route path="/login" element={<LoginPage/>}/>
                                    <Route path="/signup" element={<SignupPage/>}/>
                                    <Route path="/verify-email-pending" element={<VerifyEmailPendingPage/>}/>
                                    <Route path="/verify-email/:token" element={<EmailVerificationPage/>}/>
                                    <Route path="/confirm-email-change/:token" element={
                                        <Suspense fallback={<PageLoader/>}>
                                            <ComponentErrorBoundary errorMessage="Failed to load email change confirmation page">
                                                <ConfirmEmailChangePage/>
                                            </ComponentErrorBoundary>
                                        </Suspense>
                                    }/>

                                    <Route path="/login/golfid" element={
                                        <Suspense fallback={<PageLoader/>}>
                                            <ComponentErrorBoundary errorMessage="Failed to load Golf ID login page">
                                                <GolfIdLoginPage/>
                                            </ComponentErrorBoundary>
                                        </Suspense>
                                    }/>

                                    <Route path="/join/:code" element={
                                        <Suspense fallback={<PageLoader/>}>
                                            <ComponentErrorBoundary errorMessage="Failed to load society join page">
                                                <JoinSocietyPage/>
                                            </ComponentErrorBoundary>
                                        </Suspense>
                                    }/>

                                    <Route path="/profile/complete" element={
                                        <Suspense fallback={<PageLoader/>}>
                                            <ComponentErrorBoundary
                                                errorMessage="Failed to load profile completion page">
                                                <ProfileCompletionPage/>
                                            </ComponentErrorBoundary>
                                        </Suspense>
                                    }/>

                                    <Route path="/auth/callback" element={<OAuth2Callback/>}/>
                                    <Route path="/auth/error" element={<OAuth2Callback/>}/>

                                    <Route path="/public/tv/:competitionId" element={
                                        <Suspense fallback={<PageLoader/>}>
                                            <ComponentErrorBoundary errorMessage="Failed to load TV leaderboard">
                                                <TvLeaderboardPage/>
                                            </ComponentErrorBoundary>
                                        </Suspense>
                                    }/>

                                    <Route path="/forgot-password" element={
                                        <Suspense fallback={<PageLoader/>}>
                                            <ComponentErrorBoundary errorMessage="Failed to load forgot password page">
                                                <ForgotPasswordPage/>
                                            </ComponentErrorBoundary>
                                        </Suspense>
                                    }/>
                                    <Route path="/reset-password/:token" element={
                                        <Suspense fallback={<PageLoader/>}>
                                            <ComponentErrorBoundary errorMessage="Failed to load reset password page">
                                                <ResetPasswordPage/>
                                            </ComponentErrorBoundary>
                                        </Suspense>
                                    }/>
                                    <Route path="/reset-password" element={
                                        <Suspense fallback={<PageLoader/>}>
                                            <ComponentErrorBoundary errorMessage="Failed to load reset password page">
                                                <ResetPasswordPage/>
                                            </ComponentErrorBoundary>
                                        </Suspense>
                                    }/>

                                    <Route path="/dashboard" element={
                                        <ProtectedRoute>
                                            <Layout>
                                                <Suspense fallback={<PageLoader/>}>
                                                    <ComponentErrorBoundary errorMessage="Failed to load dashboard">
                                                        <DashboardPage/>
                                                    </ComponentErrorBoundary>
                                                </Suspense>
                                            </Layout>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/scorecard" element={
                                        <ProtectedRoute>
                                            <Suspense fallback={<PageLoader/>}>
                                                <ComponentErrorBoundary errorMessage="Failed to load scorecard">
                                                    <ScorecardPage/>
                                                </ComponentErrorBoundary>
                                            </Suspense>
                                        </ProtectedRoute>
                                    }/>
                                    <Route path="/scorecard/multiplayer" element={
                                        <ProtectedRoute>
                                            <Suspense fallback={<PageLoader/>}>
                                                <ComponentErrorBoundary errorMessage="Failed to load multiplayer round setup">
                                                    <MultiplayerRoundSetupPage/>
                                                </ComponentErrorBoundary>
                                            </Suspense>
                                        </ProtectedRoute>
                                    }/>
                                    <Route path="/scorecard/group/:groupId" element={
                                        <ProtectedRoute>
                                            <Suspense fallback={<PageLoader/>}>
                                                <ComponentErrorBoundary errorMessage="Failed to load group scoring">
                                                    <GroupScoringPage/>
                                                </ComponentErrorBoundary>
                                            </Suspense>
                                        </ProtectedRoute>
                                    }/>
                                    <Route path="/scorecard/:roundId" element={
                                        <ProtectedRoute>
                                            <Suspense fallback={<PageLoader/>}>
                                                <ComponentErrorBoundary errorMessage="Failed to load scorecard">
                                                    <ScorecardPage/>
                                                </ComponentErrorBoundary>
                                            </Suspense>
                                        </ProtectedRoute>
                                    }/>
                                    <Route path="/scorecard-view/:resultId" element={
                                        <ProtectedRoute>
                                            <Layout>
                                                <Suspense fallback={<PageLoader/>}>
                                                    <ComponentErrorBoundary
                                                        errorMessage="Failed to load scorecard view">
                                                        <ScoreCardViewPage/>
                                                    </ComponentErrorBoundary>
                                                </Suspense>
                                            </Layout>
                                        </ProtectedRoute>
                                    }/>
                                    <Route path="/profile" element={
                                        <ProtectedRoute>
                                            <Layout>
                                                <Suspense fallback={<PageLoader/>}>
                                                    <ComponentErrorBoundary errorMessage="Failed to load profile">
                                                        <ProfilePage/>
                                                    </ComponentErrorBoundary>
                                                </Suspense>
                                            </Layout>
                                        </ProtectedRoute>
                                    }/>
                                    <Route path="/profile/payments" element={
                                        <ProtectedRoute>
                                            <Layout>
                                                <Suspense fallback={<PageLoader/>}>
                                                    <ComponentErrorBoundary errorMessage="Failed to load payment history">
                                                        <PaymentHistoryPage/>
                                                    </ComponentErrorBoundary>
                                                </Suspense>
                                            </Layout>
                                        </ProtectedRoute>
                                    }/>
                                    <Route path="/profile/:userId" element={
                                        <ProtectedRoute>
                                            <Layout>
                                                <Suspense fallback={<PageLoader/>}>
                                                    <ComponentErrorBoundary errorMessage="Failed to load profile">
                                                        <ProfilePage/>
                                                    </ComponentErrorBoundary>
                                                </Suspense>
                                            </Layout>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/competitions" element={
                                        <ProtectedRoute>
                                            <Layout>
                                                <Suspense fallback={<PageLoader/>}>
                                                    <ComponentErrorBoundary errorMessage="Failed to load competitions">
                                                        <CompetitionsPage/>
                                                    </ComponentErrorBoundary>
                                                </Suspense>
                                            </Layout>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/competitions/flights/:flightId/scoring" element={
                                        <ProtectedRoute>
                                            <Suspense fallback={<PageLoader/>}>
                                                <ComponentErrorBoundary
                                                    errorMessage="Failed to load flight scoring">
                                                    <FlightScoringPage/>
                                                </ComponentErrorBoundary>
                                            </Suspense>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/competitions/flights/:flightId/review" element={
                                        <ProtectedRoute>
                                            <Suspense fallback={<PageLoader/>}>
                                                <ComponentErrorBoundary
                                                    errorMessage="Failed to load scorecard review">
                                                    <FlightScorecardReviewPage/>
                                                </ComponentErrorBoundary>
                                            </Suspense>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/competitions/:competitionId/series/:seriesId/matches/:matchId/score" element={
                                        <ProtectedRoute>
                                            <Suspense fallback={<PageLoader/>}>
                                                <ComponentErrorBoundary
                                                    errorMessage="Failed to load match scoring">
                                                    <MatchScoringPage/>
                                                </ComponentErrorBoundary>
                                            </Suspense>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/competitions/:competitionId/series/:seriesId/matches/:matchId/review" element={
                                        <ProtectedRoute>
                                            <Suspense fallback={<PageLoader/>}>
                                                <ComponentErrorBoundary
                                                    errorMessage="Failed to load match result review">
                                                    <MatchResultReviewPage/>
                                                </ComponentErrorBoundary>
                                            </Suspense>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/competitions/:id/view/:tab?" element={
                                        <ProtectedRoute>
                                            <Layout>
                                                <Suspense fallback={<PageLoader/>}>
                                                    <ComponentErrorBoundary
                                                        errorMessage="Failed to load competition view">
                                                        <CompetitionViewPage/>
                                                    </ComponentErrorBoundary>
                                                </Suspense>
                                            </Layout>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/competitions/create" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="MANAGER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load competition create page">
                                                            <CompetitionCreatePage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/competitions/:id" element={<Navigate to="edit" replace/>}/>

                                    <Route path="/competitions/:id/*" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="MANAGER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load competition edit page">
                                                            <CompetitionEditPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/rankings" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="MANAGER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load rankings">
                                                            <RankingSeriesListPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/rankings/create" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="MANAGER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load ranking form">
                                                            <RankingSeriesFormPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/rankings/:id/:tab" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="MANAGER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load ranking">
                                                            <RankingEditPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/rankings/point-systems" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="MANAGER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load point systems">
                                                            <PointSystemsPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/player/rankings" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="PLAYER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load rankings">
                                                            <PlayerRankingsPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/player/rankings/:id" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="PLAYER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load ranking leaderboard">
                                                            <PlayerRankingViewPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/administration" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="MANAGER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load administration">
                                                            <AdministrationPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/reports" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="MANAGER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load reports">
                                                            <ReportsPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/settings" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="MANAGER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary errorMessage="Failed to load settings">
                                                            <SettingsPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/tenants" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="SUPERUSER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary errorMessage="Failed to load tenants">
                                                            <TenantsPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/tenants/create" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="SUPERUSER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load tenant create page">
                                                            <TenantCreatePage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/tenants/:id/edit" element={<Navigate to="../basic" replace/>}/>

                                    <Route path="/tenants/:id/:tab" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="SUPERUSER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load tenant edit page">
                                                            <TenantEditPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/admin/scorecards" element={
                                        <ProtectedRoute>
                                            <Layout>
                                                <Suspense fallback={<PageLoader/>}>
                                                    <ComponentErrorBoundary
                                                        errorMessage="Failed to load scorecard management">
                                                        <ScorecardManagementPage/>
                                                    </ComponentErrorBoundary>
                                                </Suspense>
                                            </Layout>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Scorecard routes - :id can be 'new' or a numeric ID */}
                                    <Route path="/admin/scorecards/:id" element={<Navigate to="info" replace/>}/>
                                    <Route path="/admin/scorecards/:id/:tab" element={
                                        <ProtectedRoute>
                                            <Layout>
                                                <Suspense fallback={<PageLoader/>}>
                                                    <ComponentErrorBoundary
                                                        errorMessage="Failed to load scorecard edit page">
                                                        <ScorecardEditPage/>
                                                    </ComponentErrorBoundary>
                                                </Suspense>
                                            </Layout>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/admin/time-schedule-templates" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="MANAGER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load time schedule templates">
                                                            <TimeScheduleTemplatesManager/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/admin/time-schedule-templates/create" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="MANAGER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load time schedule template form">
                                                            <TimeScheduleTemplateCreatePage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/admin/time-schedule-templates/:id/edit" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="MANAGER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load time schedule template">
                                                            <TimeScheduleTemplateEditPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/administration/analytics/course" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="MANAGER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load course analytics">
                                                            <CourseAnalyticsPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/admin/analytics" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="MANAGER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load analytics dashboard">
                                                            <AnalyticsDashboardPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/administration/payments" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="MANAGER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load payment report">
                                                            <PaymentReportPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>
                                    <Route path="/administration/add-ons" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="MANAGER">
                                                <Layout fullWidth flushTop>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load add-on services">
                                                            <AddOnsPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>
                                    <Route path="/administration/add-ons/:tab" element={<Navigate to="/administration/add-ons" replace/>}/>
                                    <Route path="/administration/polls" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="MANAGER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load course polls">
                                                            <CoursePollsPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>
                                    <Route path="/users" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="SUPERUSER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load user management">
                                                            <AdminUsersPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/users/create" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="SUPERUSER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary errorMessage="Failed to load create user page">
                                                            <CreateUserPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/users/:id/edit" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="SUPERUSER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary errorMessage="Failed to load edit user page">
                                                            <EditUserPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/administration/handicap-recommendations" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="SUPERUSER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load handicap recommendations">
                                                            <HandicapRecommendationsPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/administration/handicap-recommendations/create" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="SUPERUSER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load handicap recommendation form">
                                                            <HandicapRecommendationCreatePage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/administration/handicap-recommendations/:id/edit" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="SUPERUSER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load handicap recommendation form">
                                                            <HandicapRecommendationEditPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/administration/email-log" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="SUPERUSER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load email log">
                                                            <EmailLogPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/friends" element={
                                        <ProtectedRoute>
                                            <Layout>
                                                <Suspense fallback={<PageLoader/>}>
                                                    <ComponentErrorBoundary errorMessage="Failed to load friends page">
                                                        <FriendsPage/>
                                                    </ComponentErrorBoundary>
                                                </Suspense>
                                            </Layout>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/friends/scorecard/:id" element={
                                        <ProtectedRoute>
                                            <Layout>
                                                <Suspense fallback={<PageLoader/>}>
                                                    <ComponentErrorBoundary
                                                        errorMessage="Failed to load friend scorecard">
                                                        <FriendScorecardPage/>
                                                    </ComponentErrorBoundary>
                                                </Suspense>
                                            </Layout>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/friends/profile/:id" element={
                                        <ProtectedRoute>
                                            <Layout>
                                                <Suspense fallback={<PageLoader/>}>
                                                    <ComponentErrorBoundary
                                                        errorMessage="Failed to load friend profile">
                                                        <FriendProfilePage/>
                                                    </ComponentErrorBoundary>
                                                </Suspense>
                                            </Layout>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/friends/connect" element={
                                        <ProtectedRoute>
                                            <Layout>
                                                <Suspense fallback={<PageLoader/>}>
                                                    <ComponentErrorBoundary errorMessage="Failed to load friends page">
                                                        <FriendsPage/>
                                                    </ComponentErrorBoundary>
                                                </Suspense>
                                            </Layout>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/stats" element={
                                        <ProtectedRoute>
                                            <Layout>
                                                <Suspense fallback={<PageLoader/>}>
                                                    <ComponentErrorBoundary errorMessage="Failed to load stats page">
                                                        <StatsDashboardPage/>
                                                    </ComponentErrorBoundary>
                                                </Suspense>
                                            </Layout>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/payment/result" element={
                                        <ProtectedRoute>
                                            <Layout>
                                                <Suspense fallback={<PageLoader/>}>
                                                    <ComponentErrorBoundary errorMessage="Failed to load payment result">
                                                        <PaymentResultPage/>
                                                    </ComponentErrorBoundary>
                                                </Suspense>
                                            </Layout>
                                        </ProtectedRoute>
                                    }/>

                                    <Route path="/competitions/categories" element={
                                        <ProtectedRoute>
                                            <RoleProtectedRoute requiredRole="MANAGER">
                                                <Layout>
                                                    <Suspense fallback={<PageLoader/>}>
                                                        <ComponentErrorBoundary
                                                            errorMessage="Failed to load competition categories">
                                                            <CategoriesPage/>
                                                        </ComponentErrorBoundary>
                                                    </Suspense>
                                                </Layout>
                                            </RoleProtectedRoute>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Public category route — ADR-059: must be before /:tenantCode/events catch-all (ADR-013) */}
                                    <Route path="/:tenantCode/events/category/:categorySlug" element={
                                        <Suspense fallback={<PageLoader/>}>
                                            <ComponentErrorBoundary errorMessage="Failed to load public calendar">
                                                <PublicCompetitionCalendarPage/>
                                            </ComponentErrorBoundary>
                                        </Suspense>
                                    }/>

                                    {/* Public ranking pages — ADR-046: must be before /:tenantCode/events catch-all (ADR-013) */}
                                    <Route path="/:tenantCode/rankings/:id" element={
                                        <Suspense fallback={<PageLoader/>}>
                                            <ComponentErrorBoundary errorMessage="Failed to load public ranking">
                                                <PublicRankingDetailPage/>
                                            </ComponentErrorBoundary>
                                        </Suspense>
                                    }/>
                                    <Route path="/:tenantCode/rankings" element={
                                        <Suspense fallback={<PageLoader/>}>
                                            <ComponentErrorBoundary errorMessage="Failed to load public rankings">
                                                <PublicRankingListPage/>
                                            </ComponentErrorBoundary>
                                        </Suspense>
                                    }/>

                                    {/* Public competition detail pages — must be before the calendar catch-all */}
                                    <Route path="/:tenantCode/events/:competitionId/:tab" element={
                                        <Suspense fallback={<PageLoader/>}>
                                            <ComponentErrorBoundary errorMessage="Failed to load public competition">
                                                <PublicCompetitionDetailPage/>
                                            </ComponentErrorBoundary>
                                        </Suspense>
                                    }/>
                                    <Route path="/:tenantCode/events/:competitionId" element={
                                        <Suspense fallback={<PageLoader/>}>
                                            <ComponentErrorBoundary errorMessage="Failed to load public competition">
                                                <PublicCompetitionDetailPage/>
                                            </ComponentErrorBoundary>
                                        </Suspense>
                                    }/>

                                    {/* Public competition calendar — MUST remain last: /:tenantCode/events is a catch-all (ADR-013 D-D) */}
                                    <Route path="/:tenantCode/events" element={
                                        <Suspense fallback={<PageLoader/>}>
                                            <ComponentErrorBoundary errorMessage="Failed to load public calendar">
                                                <PublicCompetitionCalendarPage/>
                                            </ComponentErrorBoundary>
                                        </Suspense>
                                    }/>
                                </Routes>

                                <Toaster
                                    position="top-center"
                                    toastOptions={{
                                        duration: 4000,
                                        style: {
                                            background: '#363636',
                                            color: '#fff',
                                            cursor: 'pointer',
                                        },
                                        success: {
                                            style: {
                                                background: '#198754',
                                            },
                                        },
                                        error: {
                                            style: {
                                                background: '#dc3545',
                                            },
                                        },
                                    }}
                                >
                                    {(t) => (
                                        <ToastBar toast={t}>
                                            {({icon, message}) => (
                                                <div
                                                    onClick={() => toast.dismiss(t.id)}
                                                    style={{display: 'flex', alignItems: 'center'}}
                                                >
                                                    {icon}
                                                    {message}
                                                </div>
                                            )}
                                        </ToastBar>
                                    )}
                                </Toaster>
                                <UpdateModal />
                            </div>
                        </AppInitializer>
                        </NavBackProvider>
                    </BreadcrumbProvider>
                </AppContextProvider>
            </Router>
        </ErrorBoundary>
    );

    return appContent;
}

export default App
